/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Viacheslav Smalenski
 * @version 1.28.0
 * @since 1.15.0
 */

import styled from 'styled-components';

import { colors, fonts } from '../../../../theme';
import { EInquiryStatusIds } from '../../types/inquiryInfoType';

export const InquiryInfoPageStyled = styled.div.attrs({
    className: 'inquiry-info__page',
})`
    .inquiry-info-page__title {
        ${fonts.textMainBold}
        margin-bottom: 8px;
    }

    .inquiry-info-page__subtitle {
        margin-bottom: 4px;
        color: ${colors.textColorOpacity};
    }

    .inquiry-info-page__text {
        margin-bottom: 16px;
    }
`;

export const InquiryInfoPageStatusStyled = styled.div.attrs({
    className: 'inquiry-info__status',
})<{ statusId: number }>`
    margin-bottom: 16px;
    color: ${(props) => {
        if (props.statusId === EInquiryStatusIds.IN_WORK.valueOf()) {
            return colors.textColorWarning;
        } else if (
            props.statusId === EInquiryStatusIds.WAITING_FOR_CLIENT.valueOf() ||
            props.statusId === EInquiryStatusIds.WAITING_FOR_APPROVAL.valueOf()
        ) {
            return colors.textColorDanger;
        } else if (props.statusId === EInquiryStatusIds.CLOSED.valueOf()) {
            return colors.textColorSuccess;
        } else {
            return colors.textColor;
        }
    }};
`;

export const InquiryInfoPageTitleStyled = styled.div.attrs({
    className: 'inquiry-info__page-title',
})`
    display: flex;
    justify-content: space-between;
    width: 100%;

    .inquiry-info-page__title {
        ${fonts.textHeader3}
    }

    .inquiry-info-page__button {
        max-height: 32px;
    }
`;

export const InquiryInfoPageContentStyled = styled.div.attrs({
    className: 'inquiry-info__page-content',
})`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 32px;
`;

export const InquiryInfoPageDateInfoStyled = styled.div.attrs({
    className: 'inquiry-info__date',
})`
    ${fonts.textSmall}
    color: ${colors.textColorOpacity};
`;

/*
 * -------------------------------------------
 *         CHAT HISTORY
 * -------------------------------------------
 */

export const InquiryInfoPageMissingChatHistoryStyled = styled.div.attrs({
    className: 'inquiry-info__missing-chat-history',
})`
    display: flex;
    align-items: center;
    margin-top: 16px;
`;

export const InquiryInfoPageChatHistoryAttachButtonBlockStyled = styled.div`
    display: flex;
    align-items: center;
    margin-top: 20px;
    column-gap: 10px;
`;

export const ErrorItem = styled.div`
    color: ${colors.textColorDanger};
`;

export const InquiryInfoPageMissingChatHistoryTextStyled = styled.div.attrs({
    className: 'inquiry-info__missing-chat-history-text',
})`
    margin-left: 10px;
`;

interface IInquiryInfoPageChatHistoryMissingBlock {
    isActive: boolean;
}

export const InquiryInfoPageChatHistoryMissingBlockStyled = styled.div.attrs({
    className: 'inquiry-info__chat-history-missing-block',
})<IInquiryInfoPageChatHistoryMissingBlock>`
    position: ${(props) => (props.isActive ? 'static' : 'absolute')};
    visibility: ${(props) => (props.isActive ? 'visible' : 'hidden')};
    opacity: ${(props) => (props.isActive ? 1 : 0)};
    transition: visibility 0s, opacity 2s linear;
`;

interface IInquiryInfoPageChatHistoryText {
    isOperator: boolean;
}

export const InquiryInfoPageChatHistoryNoteStyled = styled.div.attrs({
    className: 'inquiry-info__chat-history-note',
})<IInquiryInfoPageChatHistoryText>`
    padding: 12px 20px;
    overflow-y: auto;
    margin-bottom: 20px;
    background-color: ${(props) => (props.isOperator ? colors.backgroundColorModal : colors.backgroundColorWhite)};
    ${(props) => (!props.isOperator ? `border: 1px solid ${colors.borderColorSeparate};` : '')}
`;

export const InquiryInfoPageChatHistoryNoteInfoStyled = styled.div.attrs({
    className: 'inquiry-info__chat-history-note-info',
})`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
`;

export const InquiryInfoPageChatHistoryUserInfoStyled = styled.div.attrs({
    className: 'inquiry-info__chat-history-user',
})`
    color: ${colors.textColorOpacity};
`;

export const InquiryInfoAnswerNoteStyled = styled.div.attrs({
    className: 'inquiry-info__answer',
})`
    max-height: 325px;
    overflow-y: auto;
    margin-top: 20px;
    margin-bottom: 20px;

    & textarea {
        padding: 12px 20px;
        min-height: 96px;
        box-sizing: border-box;
        ${fonts.textMain};
        outline: none;
    }
`;

export const InquiryInfoAnswerFooterStyled = styled.div.attrs({
    className: 'inquiry-info__answer-footer',
})`
    display: flex;
    align-items: center;
    margin: 20px 0;
    column-gap: 10px;

    & .inquiry-info__answer-attach-file {
        height: 48px;
    }

    & .inquiry-info__answer-delete-file-icon {
        margin-bottom: 4px;
        align-self: flex-end;
    }
`;

export const InquiryInfoPageChatHistoryFinalLineStyled = styled.div.attrs({
    className: 'inquiry-info__chat-history-final-line',
})`
    height: 1px;
    background-color: ${colors.borderColorSeparate};
    text-align: center;
    margin-top: 30px;
    margin-bottom: 20px;
`;

export const InquiryInfoPageChatHistoryFinalLineTextStyled = styled.span.attrs({
    className: 'inquiry-info__chat-history-final-line-text',
})`
    background-color: white;
    position: relative;
    top: -0.8em;
    padding-left: 6px;
    padding-right: 6px;
    color: ${colors.textColorOpacity};
`;

/*
 * -------------------------------------------
 *         ATTACHED FILES
 * -------------------------------------------
 */

export const InquiryInfoPageFormContainer = styled.div`
    position: relative;
`;

export const InquiryInfoPageAttachedFileContainerStyled = styled.div.attrs({
    className: 'inquiry-info__attached-file-container',
})`
    display: flex;
    align-items: center;
    margin-right: 16px;
`;

export const InquiryInfoPageAttachedFileNameStyled = styled.div.attrs({
    className: 'inquiry-info__attached-file-name',
})`
    margin-left: 10px;
    color: ${colors.textLinkColor};
`;

export const InquiryInfoPageAttachedFileInfoStyled = styled.div.attrs({
    className: 'inquiry-info__attached-file-content',
})`
    display: flex;
    margin-left: 42px;
`;

export const InquiryInfoPageAttachedFileUserInfoStyled = styled.div.attrs({
    className: 'inquiry-info__attached-file-user',
})`
    ${fonts.textSmall}
`;
